<template>
  <div class="history-panel">
    <div class="history-header">
      <h3>对话历史</h3>
      <button @click="$emit('close')" class="close-btn">✕</button>
    </div>
    
    <!-- 搜索和筛选 -->
    <div class="history-search">
      <div class="search-bar">
        <input 
          v-model="searchQuery" 
          placeholder="搜索历史对话..."
          @input="filterHistory"
        />
        <button v-if="searchQuery" @click="clearSearch">✕</button>
      </div>
      
      <div class="filter-tabs">
        <button 
          v-for="tab in filterTabs" 
          :key="tab.value"
          :class="{ active: currentFilter === tab.value }"
          @click="setFilter(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- 历史列表 -->
    <div class="history-list">
      <div 
        v-for="item in filteredHistory" 
        :key="item.id"
        class="history-item"
        @click="loadConversation(item)"
      >
        <div class="item-header">
          <div class="title">{{ item.title }}</div>
          <div class="time">{{ formatTime(item.timestamp) }}</div>
        </div>
        
        <div class="preview">{{ getPreview(item.messages) }}</div>
        
        <div class="item-actions">
          <button @click.stop="toggleFavorite(item)" :class="{ active: item.favorite }">
            {{ item.favorite ? '⭐' : '☆' }}
          </button>
          <button @click.stop="deleteItem(item.id)">🗑️</button>
          <button @click.stop="exportItem(item)">📤</button>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="filteredHistory.length === 0" class="empty-state">
      <div class="icon">📚</div>
      <div class="text">暂无历史记录</div>
      <div class="subtext">开始对话，记录你的精彩瞬间</div>
    </div>

    <!-- 批量操作 -->
    <div v-if="history.length > 0" class="batch-actions">
      <button @click="exportAll">导出全部</button>
      <button @click="clearAll">清空全部</button>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      searchQuery: '',
      currentFilter: 'all',
      history: [],
      filterTabs: [
        { label: '全部', value: 'all' },
        { label: '收藏', value: 'favorite' },
        { label: '最近', value: 'recent' }
      ]
    }
  },
  
  computed: {
    filteredHistory() {
      let result = [...this.history]
      
      // 按筛选条件过滤
      if (this.currentFilter === 'favorite') {
        result = result.filter(item => item.favorite)
      } else if (this.currentFilter === 'recent') {
        const threeDaysAgo = Date.now() - 3 * 24 * 60 * 60 * 1000
        result = result.filter(item => new Date(item.timestamp).getTime() > threeDaysAgo)
      }
      
      // 按搜索词过滤
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        result = result.filter(item => 
          item.title.toLowerCase().includes(query) ||
          item.messages.some(msg => msg.content.toLowerCase().includes(query))
        )
      }
      
      // 按时间排序
      return result.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      )
    }
  },
  
  methods: {
    loadHistory() {
      const saved = localStorage.getItem('mimo_history')
      this.history = saved ? JSON.parse(saved) : []
    },
    
    loadConversation(item) {
      this.$emit('load', item)
      this.$emit('close')
    },
    
    toggleFavorite(item) {
      item.favorite = !item.favorite
      this.saveHistory()
      // 使用浏览器原生提示代替toast
      const message = item.favorite ? '已收藏' : '已取消收藏'
      this.showSimpleToast(message)
    },
    
    deleteItem(id) {
      if (confirm('确定删除这条记录吗？')) {
        this.history = this.history.filter(item => item.id !== id)
        this.saveHistory()
        this.showSimpleToast('已删除')
      }
    },
    
    exportItem(item) {
      const content = this.formatExportContent(item)
      this.downloadFile(`${item.title}.txt`, content)
      this.showSimpleToast('导出成功')
    },
    
    exportAll() {
      const content = this.history.map(item =>
        this.formatExportContent(item)
      ).join('\n\n' + '='.repeat(50) + '\n\n')
      
      this.downloadFile('mimo_history.txt', content)
      this.showSimpleToast('全部导出成功')
    },
    
    clearAll() {
      if (confirm('确定清空所有历史记录吗？此操作不可恢复。')) {
        this.history = []
        this.saveHistory()
        localStorage.removeItem('mimo_history')
        this.showSimpleToast('已清空')
      }
    },
    
    // 简单的提示消息方法
    showSimpleToast(message) {
      // 创建临时提示元素
      const toast = document.createElement('div')
      toast.textContent = message
      toast.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 12px 24px;
        border-radius: 8px;
        z-index: 10000;
        font-size: 14px;
        animation: fadeInOut 2s ease-in-out;
      `
      
      // 添加动画样式
      if (!document.getElementById('toast-animation')) {
        const style = document.createElement('style')
        style.id = 'toast-animation'
        style.textContent = `
          @keyframes fadeInOut {
            0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
            20% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
            80% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
            100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
          }
        `
        document.head.appendChild(style)
      }
      
      document.body.appendChild(toast)
      
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast)
        }
      }, 2000)
    },
    
    formatExportContent(item) {
      const lines = [
        `标题: ${item.title}`,
        `时间: ${this.formatTime(item.timestamp)}`,
        `收藏: ${item.favorite ? '是' : '否'}`,
        '',
        '对话内容:'
      ]
      
      item.messages.forEach(msg => {
        const role = msg.role === 'user' ? '用户' : 'Mimo'
        lines.push(`${role}: ${msg.content}`)
      })
      
      return lines.join('\n')
    },
    
    downloadFile(filename, content) {
      const blob = new Blob([content], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    },
    
    filterHistory() {
      // 触发计算属性重新计算
      this.$forceUpdate()
    },
    
    clearSearch() {
      this.searchQuery = ''
    },
    
    setFilter(filter) {
      this.currentFilter = filter
    },
    
    formatTime(timestamp) {
      const date = new Date(timestamp)
      const now = new Date()
      const diff = now - date
      
      if (diff < 60000) return '刚刚'
      if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`
      
      return date.toLocaleDateString('zh-CN')
    },
    
    getPreview(messages) {
      if (!messages || messages.length === 0) return ''
      const lastMsg = messages[messages.length - 1]
      return lastMsg.content.substring(0, 50) + (lastMsg.content.length > 50 ? '...' : '')
    },
    
    saveHistory() {
      localStorage.setItem('mimo_history', JSON.stringify(this.history))
    }
  },
  
  mounted() {
    this.loadHistory()
  }
}
</script>

<style scoped>
.history-panel {
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid rgba(255, 105, 0, 0.1);
}

.history-header h3 {
  margin: 0;
  color: #FF6900;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 4px;
}

.close-btn:hover {
  background: #f5f5f5;
}

/* 搜索区域 */
.history-search {
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
}

.search-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.search-bar input {
  flex: 1;
  border: 1px solid rgba(255, 105, 0, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
}

.search-bar input:focus {
  outline: none;
  border-color: #FF6900;
}

.search-bar button {
  background: #f5f5f5;
  border: none;
  border-radius: 8px;
  padding: 0 12px;
  cursor: pointer;
  color: #666;
}

.filter-tabs {
  display: flex;
  gap: 8px;
}

.filter-tabs button {
  flex: 1;
  border: 1px solid rgba(255, 105, 0, 0.2);
  background: white;
  border-radius: 6px;
  padding: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #666;
}

.filter-tabs button.active {
  background: #FF6900;
  color: white;
  border-color: #FF6900;
}

/* 历史列表 */
.history-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px;
}

.history-item {
  border-bottom: 1px solid #f5f5f5;
  padding: 16px 0;
  cursor: pointer;
  transition: background 0.2s ease;
}

.history-item:hover {
  background: #fafafa;
  margin: 0 -20px;
  padding: 16px 20px;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.title {
  font-weight: 600;
  color: #333;
  font-size: 15px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.time {
  font-size: 12px;
  color: #999;
  margin-left: 8px;
}

.preview {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.item-actions {
  display: flex;
  gap: 8px;
}

.item-actions button {
  background: none;
  border: 1px solid rgba(255, 105, 0, 0.2);
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #666;
}

.item-actions button:hover {
  background: #FF6900;
  color: white;
  border-color: #FF6900;
}

.item-actions button.active {
  background: #FF6900;
  color: white;
  border-color: #FF6900;
}

/* 空状态 */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: #999;
}

.empty-state .icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-state .text {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #666;
}

.empty-state .subtext {
  font-size: 13px;
  color: #999;
}

/* 批量操作 */
.batch-actions {
  padding: 16px 20px;
  border-top: 1px solid #f5f5f5;
  display: flex;
  gap: 8px;
}

.batch-actions button {
  flex: 1;
  background: white;
  border: 1px solid rgba(255, 105, 0, 0.2);
  border-radius: 8px;
  padding: 10px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #FF6900;
}

.batch-actions button:hover {
  background: #FF6900;
  color: white;
  border-color: #FF6900;
}
</style>